
// Libaries
import { Component, namespace } from "nuxt-property-decorator";

import {
  formatTopCitiesTableData,
  getTableHeaderData,
  replaceToUmlauts,
  returnPriceFormatDE,
} from "~/mixins/formatting";

// Components

import FederalStates from "~/assets/data/federalStates";
import pelletsStateContent, {
  PelletsStateConfig,
} from "~/assets/data/pelletsStateContent";

// Mixins
import Accordion from "~/components/molecules/display/Accordion.vue";
import BreadCrumb from "~/components/molecules/display/BreadCrumb.vue";
import DistrictLocationsList from "~/components/molecules/display/DistrictLocationsList.vue";
import DistrictOtherLocations from "~/components/molecules/display/DistrictOtherLocations.vue";
import EsyFigcaption from "~/components/atoms/text/EsyFigcaption.vue";
import CityListSection from "~/components/organisms/display/geographySites/CityListSection.vue";
import PelletsCurrentPriceSection from "~/components/organisms/display/pellets/PelletsCurrentPriceSection.vue";
import PelletsHeroSection from "~/components/organisms/display/pellets/PelletsHeroSection.vue";
import PelletsHowItWorks from "~/components/organisms/display/pellets/PelletsHowItWorks.vue";
import PelletsStickyComparisonBar from "~/components/organisms/display/pellets/PelletsStickyComparisonBar.vue";
import PelletsTrustBadges from "~/components/organisms/display/pellets/PelletsTrustBadges.vue";
import OilImportTable from "~/components/organisms/table/OilImportTable.vue";
import {
  createBreadCrumbsStructuredData,
  formatBreadCrumbByCountyAndBundesland,
} from "~/mixins/breadcrumb";
import PelletsChartRangeMixin from "~/mixins/PelletsChartRange";
import {
  translateStateName,
  translateStateToNutsCode,
} from "~/mixins/translations";
import { generateMetaTags } from "~/operations/meta-helper-functions";
import { PartnerType } from "~/types/Partner";
import type { PelletsDisplay } from "~/types/Pellets";
import { STANDARD_PELLETS_QUANTITY } from "~/types/Pellets";
import { isCityState } from "~/utils/geographyUtils";
import type { PelletsChartMonths } from "~/utils/pelletsChart";
import { buildPelletsDisplay } from "~/utils/pelletsDisplay";
import { calculatePelletsChartMetrics } from "~/utils/priceUtils";

const BreadCrumbModule = namespace("breadcrumb");
const FederalStatesModule = namespace("federalstates");

@Component({
  scrollToTop: true,
  components: {
    Accordion,
    BreadCrumb,
    DistrictLocationsList,
    EsyFigcaption,
    OilImportTable,
    DistrictOtherLocations,
    CityListSection,
    PelletsCurrentPriceSection,
    PelletsStickyComparisonBar,
    PelletsHeroSection,
    PelletsHowItWorks,
    PelletsTrustBadges,
  },
  methods: {
    translateStateToNutsCode,
    translateStateName,
    replaceToUmlauts,
    getTableHeaderData,
    formatTopCitiesTableData,
  },
})
export default class FederalStateView extends PelletsChartRangeMixin {
  head() {
    return {
      ...generateMetaTags(this.$route.path, "federal-states-pellets", [
        { k: "category", v: translateStateName(this.$route.params.category) },
      ]),
      link: [
        {
          rel: "canonical",
          href: `/pelletspreise/bundeslaender/${this.$route.params.category.toLowerCase()}`,
        },
      ],
      script: [
        {
          json: {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: createBreadCrumbsStructuredData(
              this.breadCrumb,
              "pellets",
            ),
          },
          type: "application/ld+json",
        },
      ],
    };
  }
  STANDARD_PELLETS_QUANTITY = STANDARD_PELLETS_QUANTITY;

  isCtaVisible = true;

  onCtaVisibilityChange(visible: boolean) {
    this.isCtaVisible = visible;
  }

  get stateConfig(): PelletsStateConfig | undefined {
    const category = this.$route.params.category;
    return pelletsStateContent[category];
  }

  get stateHeroImage(): string | null {
    return this.stateConfig?.heroImage || null;
  }

  get stateHeroImageAlt(): string {
    return (
      this.stateConfig?.heroImageAlt ||
      `Pelletspreise ${this.stateName} - Hero-Bild`
    );
  }

  @BreadCrumbModule.Mutation("setBreadCrumbForBundeslandAndCounty")
  setBreadCrumbForBundeslandAndCounty;

  @BreadCrumbModule.State("breadCrumb")
  breadCrumb;

  @FederalStatesModule.State("bigCityLinks")
  bigCityLinks;

  @FederalStatesModule.State("districtCities")
  districtCities;

  @FederalStatesModule.State("pelletsStateArticle")
  pelletsStateArticle;

  @FederalStatesModule.State("pelletsStateAssetsPrice")
  pelletsStateAssetsPrice;

  @FederalStatesModule.State("pelletsTopCities")
  pelletsTopCities;

  @FederalStatesModule.State("pelletsChartData")
  pelletsChartData;

  @FederalStatesModule.State("countyLinks")
  countyLinks;

  async fetch() {
    try {
      this.validateState();
      await this.fetchStateData();
      this.updateBreadcrumbs();
    } catch (error) {
      this.handleError(error);
    }
  }

  validateState() {
    const isValidState = FederalStates.find(
      (state) =>
        state.title.toLocaleLowerCase() ===
        replaceToUmlauts(this.$route.params.category),
    );

    if (!isValidState) {
      throw {
        statusCode: 404,
        message: "Dieses Bundesland wurde nicht gefunden",
      };
    }
  }

  async fetchStateData(): Promise<void> {
    const category = replaceToUmlauts(this.$route.params.category);
    const nutsCode = translateStateToNutsCode(category);
    const stateName = translateStateName(this.$route.params.category);

    await Promise.all([
      this.fetchCounties(nutsCode, "PARTNER_PELLET"),
      this.fetchDistrictCities(nutsCode, "PARTNER_PELLET"),
      this.fetchPelletsTopStateCities(stateName),
      this.fetchCurrentPelletsStatePrice(nutsCode),
      this.fetchCities(stateName, "PARTNER_PELLET"),
      this.fetchPelletsStateChartData(nutsCode),
      this.fetchPelletsStateArticle(stateName),
    ]);
  }

  updateBreadcrumbs() {
    const crumbs = formatBreadCrumbByCountyAndBundesland(
      this.$route.fullPath,
      this.$router,
    );

    this.setBreadCrumbForBundeslandAndCounty({
      breadCrumbData: crumbs,
      fullPath: this.$route.fullPath,
    });
  }

  handleError(error) {
    if (error.statusCode) {
      this.$nuxt.error(error);
    } else {
      console.error("Error fetching data:", error);
      this.$nuxt.error({
        statusCode: 500,
        message: "Ein unerwarteter Fehler ist aufgetreten",
      });
    }
  }

  async fetchDistrictCities(
    stateNutsCode: string,
    partnerType?: PartnerType,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchDistrictCities", {
      stateNutsCode,
      includeLinkField: true,
      partnerType,
    });
  }

  async fetchPelletsStateChartData(nutsCode: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchPelletsStateChartData", {
      nutsCode,
      monthsQuantity: this.monthsQuantity,
    });
  }

  async dispatchChartFetch(months: PelletsChartMonths): Promise<void> {
    const nutsCode = translateStateToNutsCode(
      replaceToUmlauts(this.$route.params.category),
    );
    await this.$store.dispatch("federalstates/fetchPelletsStateChartData", {
      nutsCode,
      monthsQuantity: months,
    });
  }

  async fetchPelletsStateArticle(stateName: string): Promise<void> {
    await this.$store.dispatch(
      "federalstates/fetchPelletsStateArticle",
      stateName,
    );
  }

  async fetchCities(stateName: string, partnerType: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchBigCityLinks", {
      stateName,
      partnerType,
      urlPrefix: "pelletspreise",
    });
  }

  async fetchCounties(
    statesNutsCode: string,
    partnerType?: PartnerType,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCountyLinks", {
      statesNutsCode,
      partnerType,
    });
  }

  async fetchCurrentPelletsStatePrice(nutsCode: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCurrentPelletsStatePrice", {
      nutsCode,
    });
  }

  async fetchPelletsTopStateCities(stateName: string): Promise<void> {
    await this.$store.dispatch(
      "federalstates/fetchPelletsTopStateCities",
      stateName,
    );
  }

  get pelletsMetrics() {
    return calculatePelletsChartMetrics(this.pelletsChartData);
  }

  get pelletsDisplay(): PelletsDisplay {
    return buildPelletsDisplay(
      this.pelletsStateAssetsPrice,
      this.pelletsChartData,
    );
  }

  get returnPriceFormatDE() {
    return returnPriceFormatDE;
  }

  get stateName(): string {
    return translateStateName(this.$route.params.category);
  }

  get isCityStateValue(): boolean {
    return isCityState(this.$route.params.category);
  }
}
